// GPU runners: independent worker processes that pull jobs from the studio server (the coordinator) and run
// them on their own model server (llama-swap with sd-server / audio.cpp / an OpenAI-compatible chat server).
// A runner has one or more SLOTS; a slot is an exclusive execution lane (e.g. "both GPUs of this llama-swap")
// that runs one job or streams chat answers at a time.
import { z } from "zod";
import { UserRef } from "./auth";
import {
  GenerationRequest,
  type JobProgress,
  MediaKind,
  ModelKind,
  ModelLimits,
  RedactedJob,
  SpeedMode,
} from "./core";

// ── capabilities ──────────────────────────────────────────────────────────────────────────────────────

/**
 * A slot's faster way to run a model: sd-server's step cache, set up in the runner's config (which keeps the
 * cache settings to itself). Jobs that ask for speed "fast" use it here.
 */
export const FastPath = z.object({
  /** Share of the full generation time a fast job takes (0.55: 45 % faster); scales the estimates. */
  timeFactor: z.number().gt(0).max(1),
});
export type FastPath = z.infer<typeof FastPath>;

export const SlotModel = z.object({
  /** llama-swap model id ("z-image", "minimax-h3", "minimax-music3", "qwen3.8-27b"). */
  id: z.string().min(1),
  kind: ModelKind,
  limits: ModelLimits.default({}),
  /** Chat only: OpenAI-compatible base URL the COORDINATOR can reach (it streams answers directly). */
  chatUrl: z.string().url().optional(),
  /** Chat only: bearer key for chatUrl. Sent to the coordinator, never to browsers. */
  chatApiKey: z.string().optional(),
  /** Chat only: the model sees images. */
  vision: z.boolean().optional(),
  /** Image and video: this slot can run the model fast (absent: every job runs every step). */
  fast: FastPath.optional(),
});
export type SlotModel = z.infer<typeof SlotModel>;
export type SlotModelInput = z.input<typeof SlotModel>;

export const SLOT_ID_PATTERN = /^[A-Za-z0-9_.-]{1,40}$/;

export const SlotCapabilities = z.object({
  id: z.string().regex(SLOT_ID_PATTERN, "Slot ids have 1–40 letters, digits, dots, dashes or underscores."),
  name: z.string().max(80).optional(),
  /** GPU indices (as reported in telemetry) this slot uses. */
  gpus: z.array(z.number().int()).default([]),
  /** Speed relative to the reference machine (2× RTX 3090 in LXC 100): 2 means twice as fast. Scales estimates. */
  speed: z.number().positive().default(1),
  models: z.array(SlotModel).min(1),
  /**
   * Id of another slot of the same runner whose GPUs this one uses too (e.g. one card of a pair). The two run jobs
   * at the same time only when both jobs' models are among THIS slot's models; anything else, chats included,
   * waits until the other is idle.
   */
  sharesGpusWith: z.string().regex(SLOT_ID_PATTERN).optional(),
});
export type SlotCapabilities = z.infer<typeof SlotCapabilities>;

export const RunnerCapabilities = z.object({ slots: z.array(SlotCapabilities).min(1) });
export type RunnerCapabilities = z.infer<typeof RunnerCapabilities>;
export type RunnerCapabilitiesInput = z.input<typeof RunnerCapabilities>;

// ── telemetry ─────────────────────────────────────────────────────────────────────────────────────────

/** One GPU as nvidia-smi reports it; null where the driver answers [N/A] (e.g. fans of water-cooled cards). */
export const GpuTelemetry = z.object({
  index: z.number().int(),
  name: z.string(),
  uuid: z.string().nullable(),
  utilization: z.number().nullable(),
  memoryUsedMb: z.number().nullable(),
  memoryTotalMb: z.number().nullable(),
  temperatureC: z.number().nullable(),
  powerW: z.number().nullable(),
  powerLimitW: z.number().nullable(),
  fanPercent: z.number().nullable(),
});
export type GpuTelemetry = z.infer<typeof GpuTelemetry>;

/** A telemetry sample as the coordinator keeps and publishes it. */
export const GpuSample = GpuTelemetry.extend({
  runnerId: z.string(),
  runnerName: z.string(),
  at: z.string(),
});
export type GpuSample = z.infer<typeof GpuSample>;

export const TelemetryPoint = z.object({
  at: z.string(),
  utilization: z.number().nullable(),
  memoryUsedMb: z.number().nullable(),
  temperatureC: z.number().nullable(),
  powerW: z.number().nullable(),
});
export type TelemetryPoint = z.infer<typeof TelemetryPoint>;

/** GET /api/stats/telemetry: the last hour (kept in memory by the coordinator). */
export const TelemetrySeries = z.object({
  from: z.string(),
  to: z.string(),
  series: z.array(
    z.object({
      runnerId: z.string(),
      runnerName: z.string(),
      index: z.number().int(),
      name: z.string(),
      memoryTotalMb: z.number().nullable(),
      powerLimitW: z.number().nullable(),
      points: z.array(TelemetryPoint),
    }),
  ),
});
export type TelemetrySeries = z.infer<typeof TelemetrySeries>;

// ── runner and slot state (what the web and admins see) ──────────────────────────────────────────────

export const SlotState = z.enum(["idle", "loading", "busy", "offline"]);

/**
 * LLM clients outside the studio (pi, opencode, open-webui) on a slot's GPUs, as the runner's LLM gate sees them.
 * While the LLM is busy the studio sends jobs to other GPU servers; a job only these GPUs can run waits a while,
 * then runs while the gate holds the LLM's new requests.
 */
export const LlmStatus = z.object({
  /** A request runs or waits, or the last one ended a short while ago. */
  busy: z.boolean(),
  inflight: z.number().int().min(0),
  /** Requests the gate holds while a job runs. */
  waiting: z.number().int().min(0),
  /** Seconds since the last request ended; null before the first. */
  idleSec: z.number().nullable(),
});
export type LlmStatus = z.infer<typeof LlmStatus>;
export type SlotState = z.infer<typeof SlotState>;

export const SlotInfo = z.object({
  id: z.string(),
  name: z.string().nullable(),
  gpus: z.array(z.number().int()),
  speed: z.number(),
  /** What the slot can run (chat URLs and keys are not included). */
  models: z.array(
    z.object({ id: z.string(), kind: ModelKind, limits: ModelLimits, vision: z.boolean().optional() }),
  ),
  state: SlotState,
  /** Model the slot's backend has loaded right now. */
  loadedModel: z.string().nullable(),
  busyWith: z.enum(["job", "chat"]).nullable(),
  /** The job on the slot, redacted unless it is the viewer's own. */
  job: RedactedJob.nullable(),
  /** The backend (llama-swap) answered the runner's last check. */
  backendOk: z.boolean(),
  /** LLM clients outside the studio on this slot's GPUs; null where the runner has no LLM gate. */
  llm: LlmStatus.nullable(),
});
export type SlotInfo = z.infer<typeof SlotInfo>;

export const RunnerInfo = z.object({
  id: z.string(),
  name: z.string(),
  online: z.boolean(),
  disabled: z.boolean(),
  version: z.string().nullable(),
  hostname: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string().nullable(),
  /** null: available to everyone. Otherwise only these people's jobs and chats run here. */
  reservedFor: z.array(UserRef).nullable(),
  slots: z.array(SlotInfo),
  gpus: z.array(GpuSample),
  /** Model downloads of an appliance runner (phase 2), null when nothing is downloading. */
  downloads: z
    .object({
      state: z.enum(["downloading", "verifying", "done", "failed"]),
      doneBytes: z.number(),
      totalBytes: z.number(),
      currentFile: z.string().nullable(),
      error: z.string().nullable(),
    })
    .nullable(),
  /** Models the runner could not enable, with the reason ("needs 24 GB of GPU memory, has 16 GB"). */
  unavailable: z.array(z.object({ model: z.string(), reason: z.string() })),
});
export type RunnerInfo = z.infer<typeof RunnerInfo>;

// ── admin: runner records ─────────────────────────────────────────────────────────────────────────────

export const CreateRunnerBody = z.object({
  name: z.string().trim().min(1, "Name the GPU server.").max(60),
  /** User ids; omit or null for a runner everyone can use. */
  reservedFor: z.array(z.string()).max(20).nullable().optional(),
});
export type CreateRunnerBody = z.infer<typeof CreateRunnerBody>;

export const UpdateRunnerBody = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  disabled: z.boolean().optional(),
  /** User ids allowed on this runner; null or [] = everyone. */
  reservedFor: z.array(z.string()).max(20).nullable().optional(),
  /** Make a new token (the old one stops working at once). */
  rotateToken: z.boolean().optional(),
});
export type UpdateRunnerBody = z.infer<typeof UpdateRunnerBody>;

/** The token is returned only when it was created (create, rotateToken). */
export const RunnerWithToken = z.object({ runner: RunnerInfo, token: z.string().nullable() });
export type RunnerWithToken = z.infer<typeof RunnerWithToken>;

// ── runner protocol (runner → coordinator, Authorization: Bearer <runner token>) ─────────────────────

/** What a runner reports about one of its slots. */
export const SlotStatus = z.object({
  id: z.string(),
  loadedModel: z.string().nullable(),
  /** llama-swap state of the loaded model: "ready", "starting", "stopping"… */
  loadedState: z.string().nullable(),
  /** Job the runner is executing on this slot (lets a restarted coordinator re-attach it). */
  jobId: z.string().nullable(),
  backendOk: z.boolean(),
  /** Set on the slots the runner's LLM gate guards. */
  llm: LlmStatus.nullable().optional(),
});
export type SlotStatus = z.infer<typeof SlotStatus>;

export const DownloadProgress = RunnerInfo.shape.downloads;
/** How far an appliance runner's weight downloads are (null when nothing is downloading). */
export type DownloadProgress = z.infer<typeof DownloadProgress>;

export const RunnerRegisterBody = z.object({
  name: z.string().min(1).max(60),
  version: z.string().max(40),
  hostname: z.string().max(120).optional(),
  capabilities: RunnerCapabilities,
  slots: z.array(SlotStatus),
  gpus: z.array(GpuTelemetry).default([]),
  downloads: DownloadProgress.optional(),
  unavailable: z.array(z.object({ model: z.string(), reason: z.string() })).default([]),
});
export type RunnerRegisterBody = z.infer<typeof RunnerRegisterBody>;
export type RunnerRegisterInput = z.input<typeof RunnerRegisterBody>;

export const RunnerRegisterResult = z.object({
  runnerId: z.string(),
  runnerName: z.string(),
  /** Send this with every later call; a new register starts a new session. */
  sessionId: z.string(),
  heartbeatSec: z.number(),
  /** Without a heartbeat for this long the coordinator gives the runner's jobs up. */
  leaseSec: z.number(),
  /** Longest a claim is held open. */
  claimWaitSec: z.number(),
});
export type RunnerRegisterResult = z.infer<typeof RunnerRegisterResult>;

export const RunnerHeartbeatBody = z.object({
  sessionId: z.string(),
  /** Only when they changed. */
  capabilities: RunnerCapabilities.optional(),
  slots: z.array(SlotStatus),
  gpus: z.array(GpuTelemetry).default([]),
  downloads: DownloadProgress.optional(),
  unavailable: z.array(z.object({ model: z.string(), reason: z.string() })).optional(),
});
export type RunnerHeartbeatBody = z.infer<typeof RunnerHeartbeatBody>;
export type RunnerHeartbeatInput = z.input<typeof RunnerHeartbeatBody>;

export const RunnerClaimBody = z.object({
  sessionId: z.string(),
  slotId: z.string(),
  /** Long-poll: hold the request up to this many seconds while nothing is queued (capped by the server). */
  waitSec: z.number().min(0).max(60).default(25),
});
export type RunnerClaimBody = z.infer<typeof RunnerClaimBody>;

/**
 * `ref_audio` is a sound a video should follow; `voice_ref` is the recording a speech job clones, which the
 * runner treats quite differently — it converts it and sends it inline. Two roles, so neither path has to ask
 * what kind of job it belongs to.
 */
export const InputRole = z.enum([
  "first_frame",
  "last_frame",
  "ref_image",
  "ref_video",
  "ref_audio",
  "voice_ref",
  /** The picture or video an enhancement job restores. */
  "source",
]);
export type InputRole = z.infer<typeof InputRole>;

/** An asset a job needs; download it from `url` (coordinator-relative) with the runner token. */
export const RunnerInput = z.object({
  assetId: z.string(),
  role: InputRole,
  kind: MediaKind,
  mime: z.string(),
  /** File extension including the dot (".png"). */
  ext: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  durationSec: z.number().nullable(),
  hasAudio: z.boolean(),
  size: z.number().int(),
  url: z.string(),
});
export type RunnerInput = z.infer<typeof RunnerInput>;

export const RunnerJob = z.object({
  id: z.string(),
  type: z.enum(["image", "video", "music", "speech", "enhance"]),
  model: z.string(),
  request: GenerationRequest,
  inputs: z.array(RunnerInput),
  /** Estimated generation seconds on this slot (without a model load). */
  estSec: z.number(),
  /** "fast" only when the request asks for it and this slot has a fast path for the model; else every step. */
  speed: SpeedMode,
});
export type RunnerJob = z.infer<typeof RunnerJob>;

export const RunnerClaimResult = z.object({ job: RunnerJob.nullable() });
export type RunnerClaimResult = z.infer<typeof RunnerClaimResult>;

export const RunnerProgressBody = z.object({
  sessionId: z.string(),
  phase: z.string().max(80).optional(),
  step: z.number().int().nullable().optional(),
  totalSteps: z.number().int().nullable().optional(),
  /** The model is loaded and generating (loading_model → running). */
  generating: z.boolean().optional(),
});
export type RunnerProgressBody = z.infer<typeof RunnerProgressBody>;

/** Multipart field `meta` of POST /api/runner/jobs/:id/results (files: `file`, optional `original`). */
export const RunnerResultMeta = z.object({
  sessionId: z.string(),
  /** 0-based result number (image batches). */
  index: z.number().int().min(0),
  /** What the model was given when a prompt upsampler rewrote the prompt first. */
  revisedPrompt: z.string().max(40_000).optional(),
});
export type RunnerResultMeta = z.infer<typeof RunnerResultMeta>;

export const RunnerResultUploaded = z.object({ assetId: z.string() });
export type RunnerResultUploaded = z.infer<typeof RunnerResultUploaded>;

/** Durations measured by the runner (ms), so clock differences between machines do not matter. */
export const RunnerTimings = z.object({
  prepareMs: z.number().min(0).default(0),
  /** Waiting for the model to load (0 when it was already loaded). */
  loadMs: z.number().min(0).default(0),
  generateMs: z.number().min(0).default(0),
  saveMs: z.number().min(0).default(0),
  uploadMs: z.number().min(0).default(0),
  /** The model had to be loaded for this job. */
  modelLoaded: z.boolean().default(false),
});
export type RunnerTimings = z.infer<typeof RunnerTimings>;

export const RunnerCompleteBody = z.object({
  sessionId: z.string(),
  assetIds: z.array(z.string()).min(1),
  timings: RunnerTimings,
  /** What the runner actually ran; left out by runners that predate it, which never ran anything fast. */
  speed: SpeedMode.optional(),
});
export type RunnerCompleteBody = z.infer<typeof RunnerCompleteBody>;
export type RunnerCompleteInput = z.input<typeof RunnerCompleteBody>;

export const RunnerFailBody = z.object({
  sessionId: z.string(),
  /** Written for the person; shown verbatim. */
  error: z.string().min(1).max(2000),
  /** The model had started generating (a lost job is then not re-queued). */
  generating: z.boolean().default(false),
  /** The GPU server broke rather than the request, so the studio may give the job to another server once. */
  retryElsewhere: z.boolean().default(false),
  timings: RunnerTimings.optional(),
});
export type RunnerFailBody = z.infer<typeof RunnerFailBody>;
export type RunnerFailInput = z.input<typeof RunnerFailBody>;

/** Error answers to runners carry a code besides the message. */
export const RunnerErrorCode = z.enum([
  /** Unknown session (the coordinator restarted or another process registered with this token): register again. */
  "reregister",
  /** The job is no longer this runner's (lease expired, or it was failed): drop it. */
  "lease_lost",
]);
export type RunnerErrorCode = z.infer<typeof RunnerErrorCode>;

export type JobProgressPatch = Partial<JobProgress>;
